import React, { useState, useRef } from "react";
import {
  X,
  FileText,
  Clipboard,
  ShoppingCart,
  GitCompare,
  Download,
  Printer,
  Mail,
  MessageSquare,
  Link2,
  Copy,
  Check,
  Loader2,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Shield,
  Clock,
  Wrench,
  HardHat,
  Award,
  Star,
} from "lucide-react";

/**
 * UNIVERSAL DOCUMENT GENERATOR - Preview Modal
 * 
 * Enhanced with "Handoff Standards":
 * - Client Brief: Project Overview, Timeline, Why Us (Veteran-Owned)
 * - Operator Order: Critical Steps, Required Tools, Safety, QC Checklist
 * - Scope of Work: Exclusions, Change Order Procedures, Payment Schedule
 */
const DocumentPreviewModal = ({
  isOpen,
  onClose,
  documentType,
  documentData,
  clientInfo,
  estimateData,
  onSend,
  onExport,
}) => {
  const [activeTab, setActiveTab] = useState("preview");
  const [isCopied, setIsCopied] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendMethod, setSendMethod] = useState(null);
  const contentRef = useRef(null);

  if (!isOpen) return null;

  const documentConfig = {
    client_proposal: {
      title: "Client Brief / Proposal",
      icon: FileText,
      color: "emerald",
      description: "Professional proposal with project vision",
    },
    work_order: {
      title: "Operator Order",
      icon: Clipboard,
      color: "blue",
      description: "Mission brief with execution steps & safety",
    },
    material_list: {
      title: "Logistics Manifest",
      icon: ShoppingCart,
      color: "emerald",
      description: "Material procurement list - tactical format",
    },
    change_order: {
      title: "Change Order",
      icon: GitCompare,
      color: "purple",
      description: "Scope modification documentation",
    },
    build_ops_order: {
      title: "Build Ops Order",
      icon: HardHat,
      color: "emerald",
      description: "Complete project execution plan with all scopes",
    },
  };

  const config = documentConfig[documentType] || documentConfig.client_proposal;
  const IconComponent = config.icon;

  const handleCopyLink = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/documents/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          document_type: documentType,
          estimate_id: estimateData?.estimate_id,
          lead_id: clientInfo?.lead_id,
          send_method: 'link',
          document_content: estimateData,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        await navigator.clipboard.writeText(data.share_url);
      } else {
        const link = `${window.location.origin}/view/doc_${Date.now()}`;
        await navigator.clipboard.writeText(link);
      }
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      const link = `${window.location.origin}/view/doc_${Date.now()}`;
      navigator.clipboard.writeText(link);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleSend = async (method) => {
    setIsSending(true);
    setSendMethod(method);
    try {
      if (onSend) {
        await onSend(method, documentData);
      }
    } finally {
      setIsSending(false);
      setSendMethod(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date || Date.now()).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Render document content based on type
  const renderDocumentContent = () => {
    switch (documentType) {
      case "client_proposal":
        return renderClientBrief();
      case "work_order":
        return renderOperatorOrder();
      case "material_list":
        return renderMaterialList();
      case "change_order":
        return renderScopeOfWork();
      case "build_ops_order":
        return renderBuildOpsOrder();
      default:
        return renderClientBrief();
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CLIENT BRIEF (The Pitch) - Handoff Standard
  // ═══════════════════════════════════════════════════════════════════════════
  const renderClientBrief = () => {
    const groups = estimateData?.groups || [];
    const totalCost = estimateData?.totalCost || 0;
    const totalItems = groups.reduce((sum, g) => sum + (g.items?.length || 0), 0);
    
    return (
      <div className="bg-white text-slate-900 p-8 min-h-[800px]">
        {/* Header with Badge */}
        <div className="border-b-2 border-emerald-500 pb-6 mb-8">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-5 w-5 text-emerald-600" />
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Veteran-Owned & Operated</span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900">PROJECT PROPOSAL</h1>
              <p className="text-sm text-slate-500 mt-1">Professional Scope of Work</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Proposal Date</p>
              <p className="font-bold text-lg">{formatDate()}</p>
              <p className="text-xs text-slate-400 mt-1">Valid for 30 Days</p>
            </div>
          </div>
        </div>

        {/* Client Info */}
        <div className="mb-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Prepared For</h3>
          <p className="font-bold text-xl text-slate-900">{clientInfo?.name || "Client Name"}</p>
          {clientInfo?.address && (
            <p className="text-sm text-slate-600 flex items-center gap-2 mt-2">
              <MapPin className="h-4 w-4 text-slate-400" />
              {clientInfo.address}
            </p>
          )}
          {clientInfo?.phone && (
            <p className="text-sm text-slate-600 flex items-center gap-2 mt-1">
              <Phone className="h-4 w-4 text-slate-400" />
              {clientInfo.phone}
            </p>
          )}
        </div>

        {/* PROJECT OVERVIEW - The Vision Narrative */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-slate-900 border-b border-slate-200 pb-2 mb-4 flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            Project Overview
          </h2>
          <div className="prose prose-sm text-slate-700 leading-relaxed">
            <p>
              We are pleased to present this comprehensive proposal for your project. Our team of experienced 
              craftsmen will transform your space with quality materials and expert workmanship.
            </p>
            <p className="mt-3">
              This proposal includes <strong>{totalItems} line items</strong> across <strong>{groups.length} trade categories</strong>, 
              carefully estimated to deliver exceptional results while respecting your budget.
            </p>
          </div>
        </div>

        {/* TIMELINE & MILESTONES */}
        <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-bold text-blue-900 mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Timeline & Milestones
          </h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center mx-auto mb-2 text-sm font-bold">1</div>
              <p className="text-xs font-semibold text-blue-900">Demo & Prep</p>
              <p className="text-xs text-blue-600">Days 1-2</p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center mx-auto mb-2 text-sm font-bold">2</div>
              <p className="text-xs font-semibold text-blue-900">Rough-In</p>
              <p className="text-xs text-blue-600">Days 3-5</p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center mx-auto mb-2 text-sm font-bold">3</div>
              <p className="text-xs font-semibold text-blue-900">Finishes</p>
              <p className="text-xs text-blue-600">Days 6-8</p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto mb-2 text-sm font-bold">✓</div>
              <p className="text-xs font-semibold text-emerald-700">Final Walkthrough</p>
              <p className="text-xs text-emerald-600">Day 9-10</p>
            </div>
          </div>
        </div>

        {/* Detailed Scope by Trade */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4 pb-2 border-b border-slate-200">Detailed Scope by Trade</h2>
          {groups.map((group, idx) => (
            <div key={idx} className="mb-6">
              <h3 className="font-bold text-slate-800 bg-slate-100 px-3 py-2 rounded">
                {group.name}
              </h3>
              <ul className="mt-2 space-y-1 pl-4">
                {(group.items || []).map((item, itemIdx) => (
                  <li key={itemIdx} className="text-sm text-slate-700 flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>
                      {item.itemName}
                      {item.quantity > 1 && <span className="text-slate-500"> (Qty: {item.quantity})</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Investment Summary */}
        <div className="mb-8 p-6 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
          <h3 className="text-sm font-bold text-emerald-800 mb-4">Your Investment</h3>
          <div className="flex justify-between items-center">
            <span className="text-lg text-slate-700">Total Project Cost</span>
            <span className="text-3xl font-bold text-emerald-600">{formatCurrency(totalCost)}</span>
          </div>
        </div>

        {/* WHY US - Veteran-Owned Section */}
        <div className="mb-8 p-4 bg-slate-900 text-white rounded-lg">
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4 text-amber-400" />
            Why Choose Us
          </h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <Award className="h-4 w-4 text-amber-400 mt-0.5" />
              <span><strong>Veteran-Owned & Operated</strong> - Military precision and discipline in every project</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5" />
              <span><strong>Licensed & Insured</strong> - Full coverage for your peace of mind</span>
            </li>
            <li className="flex items-start gap-2">
              <Star className="h-4 w-4 text-amber-400 mt-0.5" />
              <span><strong>30+ Years Combined Experience</strong> - Master craftsmen on every job</span>
            </li>
          </ul>
        </div>

        {/* Terms */}
        <div className="bg-slate-50 rounded-lg p-4 text-xs text-slate-600">
          <h3 className="font-bold text-slate-800 mb-2">Terms & Conditions</h3>
          <ul className="space-y-1">
            <li>• 50% deposit required to begin work</li>
            <li>• Progress payments as milestones are completed</li>
            <li>• Balance due upon final walkthrough approval</li>
            <li>• Proposal valid for 30 days from date above</li>
            <li>• Changes to scope require written Change Order</li>
          </ul>
        </div>

        {/* Signature */}
        <div className="mt-8 pt-8 border-t-2 border-slate-200">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-xs text-slate-500 mb-8">Client Approval Signature</p>
              <div className="border-b border-slate-400 mb-2" />
              <p className="text-sm">{clientInfo?.name || "Client Name"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-8">Date</p>
              <div className="border-b border-slate-400 mb-2" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // OPERATOR ORDER (The Mission) - Handoff Standard
  // ═══════════════════════════════════════════════════════════════════════════
  const renderOperatorOrder = () => {
    const groups = estimateData?.groups || [];
    
    // Trade-specific execution data
    const tradeExecutionData = {
      PLUMBING: {
        criticalSteps: ["Shut off water main", "Verify drain slope (1/4\" per foot)", "Pressure test all connections"],
        requiredTools: ["Pipe wrench set", "PEX crimper", "Torch kit", "Level", "Thread tape"],
        safetyProtocols: ["PPE required: Safety glasses, gloves", "Verify no live electrical near water", "Test for gas leaks"],
      },
      ELECTRICAL: {
        criticalSteps: ["Kill power at breaker", "Test with multimeter before touching", "Follow NEC code for wire gauge"],
        requiredTools: ["Multimeter", "Wire strippers", "Fish tape", "Voltage tester", "Conduit bender"],
        safetyProtocols: ["LOCK OUT / TAG OUT required", "Never work on live circuits", "Ground all connections"],
      },
      FLOORING: {
        criticalSteps: ["Acclimate material 48hrs", "Check subfloor flatness (1/8\" in 10')", "Stagger joints min 6\""],
        requiredTools: ["Tapping block", "Pull bar", "Spacers", "Saw (miter/jig)", "Underlayment"],
        safetyProtocols: ["Knee pads required", "Dust mask for cutting", "Proper ventilation for adhesives"],
      },
      DEMOLITION: {
        criticalSteps: ["Identify load-bearing walls", "Check for asbestos in pre-1980 homes", "Protect adjacent areas"],
        requiredTools: ["Pry bar", "Reciprocating saw", "Sledge hammer", "Dust barriers", "Dumpster"],
        safetyProtocols: ["Hard hat required", "Dust mask N95+", "Eye protection", "Check for hidden utilities"],
      },
      DEFAULT: {
        criticalSteps: ["Review scope before starting", "Verify material quantities", "Document pre-existing conditions"],
        requiredTools: ["Basic hand tools", "Measuring tape", "Level", "Pencil/marker"],
        safetyProtocols: ["PPE as needed", "Keep work area clean", "Report hazards immediately"],
      },
    };

    const getTradeData = (groupName) => {
      const upperName = groupName.toUpperCase();
      if (upperName.includes("PLUMB")) return tradeExecutionData.PLUMBING;
      if (upperName.includes("ELEC")) return tradeExecutionData.ELECTRICAL;
      if (upperName.includes("FLOOR") || upperName.includes("TILE")) return tradeExecutionData.FLOORING;
      if (upperName.includes("DEMO")) return tradeExecutionData.DEMOLITION;
      return tradeExecutionData.DEFAULT;
    };

    return (
      <div className="bg-white text-slate-900 p-8 min-h-[800px]">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg -mx-8 -mt-8 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <HardHat className="h-6 w-6" />
              <div>
                <h1 className="text-xl font-bold">OPERATOR ORDER</h1>
                <p className="text-blue-200 text-sm">Mission Brief - Crew Instructions</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-blue-200">Issue Date</p>
              <p className="font-semibold">{formatDate()}</p>
            </div>
          </div>
        </div>

        {/* Job Info */}
        <div className="bg-slate-100 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-500 font-semibold">CLIENT</p>
              <p className="font-bold">{clientInfo?.name || "Client"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-semibold">JOB SITE</p>
              <p className="font-bold">{clientInfo?.address || "TBD"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-semibold">CONTACT</p>
              <p className="font-bold">{clientInfo?.phone || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Work Sections with Enhanced Data */}
        {groups.map((group, idx) => {
          const tradeData = getTradeData(group.name);
          
          return (
            <div key={idx} className="mb-8 border border-slate-200 rounded-lg overflow-hidden">
              {/* Section Header */}
              <div className="bg-blue-100 px-4 py-3 font-bold text-blue-900 flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                {group.name}
              </div>
              
              {/* Critical Execution Steps */}
              <div className="p-4 bg-red-50 border-b border-slate-200">
                <h4 className="text-xs font-bold text-red-800 uppercase mb-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Critical Execution Steps
                </h4>
                <ol className="text-sm text-red-900 space-y-1 list-decimal list-inside">
                  {tradeData.criticalSteps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
              
              {/* Required Tools */}
              <div className="p-4 bg-amber-50 border-b border-slate-200">
                <h4 className="text-xs font-bold text-amber-800 uppercase mb-2">Required Tools</h4>
                <div className="flex flex-wrap gap-2">
                  {tradeData.requiredTools.map((tool, i) => (
                    <span key={i} className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Safety Protocols */}
              <div className="p-4 bg-emerald-50 border-b border-slate-200">
                <h4 className="text-xs font-bold text-emerald-800 uppercase mb-2 flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  Safety Protocols
                </h4>
                <ul className="text-sm text-emerald-900 space-y-1">
                  {tradeData.safetyProtocols.map((protocol, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                      {protocol}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Task List - NO PRICES */}
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-500 border-b bg-slate-50">
                    <th className="text-left py-2 px-4 w-12">#</th>
                    <th className="text-left py-2 px-4">Task Description</th>
                    <th className="text-center py-2 px-4 w-20">Qty</th>
                    <th className="text-center py-2 px-4 w-16">Unit</th>
                    <th className="text-center py-2 px-4 w-16">Done</th>
                  </tr>
                </thead>
                <tbody>
                  {(group.items || []).map((item, itemIdx) => (
                    <tr key={itemIdx} className="border-b border-slate-100">
                      <td className="py-2 px-4 text-sm text-slate-500">{itemIdx + 1}</td>
                      <td className="py-2 px-4 text-sm font-medium">{item.itemName}</td>
                      <td className="py-2 px-4 text-sm text-center">{item.quantity}</td>
                      <td className="py-2 px-4 text-sm text-center text-slate-500">{item.unit}</td>
                      <td className="py-2 px-4 text-center">
                        <div className="w-5 h-5 border-2 border-slate-300 rounded mx-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Quality Control Checklist */}
              <div className="p-4 bg-blue-50">
                <h4 className="text-xs font-bold text-blue-800 uppercase mb-2">Quality Control Checklist</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="rounded border-slate-300" />
                    <span>Work matches scope</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="rounded border-slate-300" />
                    <span>Area cleaned</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="rounded border-slate-300" />
                    <span>Photo documentation</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="rounded border-slate-300" />
                    <span>Client notified</span>
                  </label>
                </div>
              </div>
            </div>
          );
        })}

        {/* Foreman Notes */}
        <div className="mt-8 border-t border-slate-200 pt-4">
          <h3 className="font-bold text-sm mb-2">Foreman Notes</h3>
          <div className="border-2 border-dashed border-slate-300 rounded p-4 min-h-[100px]">
            <p className="text-xs text-slate-400">Document issues, changes, or client communications...</p>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LOGISTICS MANIFEST (Material List) - Tactical Dark Mode with Trade Categories
  // ═══════════════════════════════════════════════════════════════════════════
  const renderMaterialList = () => {
    const groups = estimateData?.groups || [];
    
    // More precise labor detection - only filter items that are CLEARLY labor
    const isLaborItem = (item) => {
      const name = (item.itemName || '').toLowerCase();
      
      // Check if explicitly marked as Labor type
      const costType = (item.costType || "").toLowerCase();
      if (costType === "labor" || costType === "lbr") return true;
      
      // Labor keywords that are definitive (not ambiguous)
      const definiteLaborKeywords = [
        'labor', 'labour', 'installation labor', 'install labor',
        'removal labor', 'demo labor', 'haul-off', 'hauling',
        'cleanup', 'clean-up', 'prep work', 'supervision',
        'permit fee', 'inspection fee', 'management fee',
        'demolition', ' demo ', 'protection',
      ];
      
      // Check for definitive labor terms
      if (definiteLaborKeywords.some(keyword => name.includes(keyword))) return true;
      
      // Check for pattern: ends with "labor" or "work" or "installation"
      if (name.endsWith(' labor') || name.endsWith(' work') || name.endsWith(' installation')) return true;
      
      // Check for pattern: starts with "install " or "remove " (action verbs)
      if (name.startsWith('install ') || name.startsWith('remove ') || 
          name.startsWith('demo ') || name.startsWith('haul ')) return true;
      
      return false;
    };
    
    // ═══════════════════════════════════════════════════════════════════
    // TRADE CATEGORY DEFINITIONS - Tier 1 Standard Groupings
    // ═══════════════════════════════════════════════════════════════════
    const TRADE_CATEGORIES = {
      'FRAMING & STRUCTURE': {
        keywords: ['stud', 'lumber', 'plywood', 'osb', 'blocking', 'header', '2x4', '2x6', 'joist', 'beam', 'framing'],
        icon: '🏗️',
        order: 1,
      },
      'DRYWALL & TAPING': {
        keywords: ['drywall', 'sheetrock', 'gypsum', 'joint compound', 'tape', 'corner bead', 'mud'],
        icon: '📋',
        order: 2,
      },
      'FLOORING': {
        keywords: ['floor', 'tile', 'lvp', 'carpet', 'hardwood', 'vinyl', 'laminate', 'underlayment', 'thinset', 'grout', 'mortar'],
        icon: '🪵',
        order: 3,
      },
      'PAINT & MILLWORK': {
        keywords: ['paint', 'primer', 'caulk', 'baseboard', 'trim', 'molding', 'crown', 'stain', 'polyurethane', 'millwork'],
        icon: '🎨',
        order: 4,
      },
      'WINDOWS & DOORS': {
        keywords: ['window', 'door', 'bifold', 'slider', 'entry', 'passage', 'privacy', 'lever', 'knob', 'hinge', 'threshold', 'weatherstrip'],
        icon: '🚪',
        order: 5,
      },
      'PLUMBING': {
        keywords: ['toilet', 'vanity', 'faucet', 'sink', 'shower', 'tub', 'supply line', 'p-trap', 'wax ring', 'valve', 'pipe', 'pex', 'drain'],
        icon: '🚿',
        order: 6,
      },
      'ELECTRICAL': {
        keywords: ['outlet', 'switch', 'receptacle', 'wire', 'cable', 'panel', 'breaker', 'light', 'fixture', 'fan', 'dimmer', 'junction'],
        icon: '⚡',
        order: 7,
      },
      'GENERAL MATERIALS': {
        keywords: [],
        icon: '📦',
        order: 8,
      },
    };
    
    // Categorize item into trade category
    const categorizeItem = (item) => {
      const name = (item.itemName || '').toLowerCase();
      
      for (const [category, config] of Object.entries(TRADE_CATEGORIES)) {
        if (config.keywords.some(keyword => name.includes(keyword))) {
          return category;
        }
      }
      return 'GENERAL MATERIALS';
    };
    
    // Collect ALL material items from ALL groups
    const materialItems = [];
    
    groups.forEach((group) => {
      (group.items || []).forEach(item => {
        // Skip if it's clearly a labor item
        if (isLaborItem(item)) return;
        
        const qty = parseFloat(item.quantity) || 0;
        const unitCost = parseFloat(item.unitCost) || 0;
        const markup = parseFloat(item.markupPercent) || 20;
        const builderCost = qty * unitCost;
        const clientPrice = builderCost * (1 + markup / 100);
        
        materialItems.push({ 
          ...item, 
          section: group.name,
          tradeCategory: categorizeItem(item),
          builderCost,
          clientPrice,
        });
      });
    });

    // Group by TRADE CATEGORY (not by original section)
    const tradeGroups = {};
    materialItems.forEach(item => {
      const cat = item.tradeCategory;
      if (!tradeGroups[cat]) tradeGroups[cat] = [];
      tradeGroups[cat].push(item);
    });
    
    // Sort trade groups by order
    const sortedTradeCategories = Object.entries(tradeGroups)
      .sort((a, b) => (TRADE_CATEGORIES[a[0]]?.order || 99) - (TRADE_CATEGORIES[b[0]]?.order || 99));
    
    // Calculate grand totals
    const grandTotalBuilder = materialItems.reduce((sum, i) => sum + (i.builderCost || 0), 0);
    const grandTotalClient = materialItems.reduce((sum, i) => sum + (i.clientPrice || 0), 0);

    return (
      <div 
        className="min-h-[800px] p-6"
        style={{ 
          backgroundColor: '#1a1a1a',
          fontFamily: "'Courier New', Courier, monospace",
        }}
      >
        {/* Header - Black with Neon Green Border */}
        <div 
          className="-mx-6 -mt-6 mb-6 px-6 py-4"
          style={{
            backgroundColor: '#0a0a0a',
            borderBottom: '3px solid #10b981',
          }}
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 
                className="text-xl font-bold tracking-wider"
                style={{ color: '#10b981' }}
              >
                LOGISTICS MANIFEST
              </h1>
              <p className="text-xs text-slate-400 tracking-widest uppercase">
                Material Procurement List • {materialItems.length} Line Items • {sortedTradeCategories.length} Trade Categories
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Manifest Date</p>
              <p className="text-sm font-bold text-slate-200">{formatDate()}</p>
            </div>
          </div>
        </div>

        {/* Delivery Intel */}
        <div 
          className="mb-6 p-4 rounded-lg border"
          style={{
            backgroundColor: '#252525',
            borderColor: '#3f3f3f',
          }}
        >
          <h3 
            className="text-xs font-bold uppercase tracking-wider mb-3"
            style={{ color: '#10b981' }}
          >
            ▸ Delivery Intel
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-500 uppercase">Drop Zone</p>
              <p className="text-slate-200 font-medium">{clientInfo?.address || "Job Site - TBD"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Point of Contact</p>
              <p className="text-slate-200 font-medium">{clientInfo?.phone || "Confirm on arrival"}</p>
            </div>
          </div>
        </div>

        {/* Trade Categories with Collapsible Sections */}
        {sortedTradeCategories.map(([category, items], idx) => {
          const categoryConfig = TRADE_CATEGORIES[category] || {};
          const categoryBuilderTotal = items.reduce((sum, i) => sum + (i.builderCost || 0), 0);
          const categoryClientTotal = items.reduce((sum, i) => sum + (i.clientPrice || 0), 0);
          
          return (
            <div key={idx} className="mb-5">
              {/* Category Header with Icon */}
              <div 
                className="px-4 py-3 rounded-t flex justify-between items-center"
                style={{ backgroundColor: '#2a2a2a', borderLeft: '4px solid #10b981' }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">{categoryConfig.icon || '📦'}</span>
                  <span className="text-sm font-bold text-slate-200 uppercase tracking-wide">
                    {category}
                  </span>
                  <span className="text-xs text-slate-500 ml-2">
                    ({items.length} items)
                  </span>
                </div>
              </div>
              
              {/* Items Table with IMPROVED STYLING */}
              <table className="w-full" style={{ backgroundColor: '#1f1f1f' }}>
                <thead>
                  <tr 
                    className="text-xs uppercase tracking-wider"
                    style={{ backgroundColor: '#252525', color: '#6b7280' }}
                  >
                    <th className="text-left py-2 px-3 border-b border-slate-700" style={{ width: '45%' }}>Item Description</th>
                    <th className="text-center py-2 px-2 border-b border-slate-700" style={{ width: '10%', verticalAlign: 'middle' }}>Qty</th>
                    <th className="text-center py-2 px-2 border-b border-slate-700" style={{ width: '10%', verticalAlign: 'middle' }}>Unit</th>
                    <th className="text-right py-2 px-3 border-b border-slate-700" style={{ width: '15%' }}>Builder Cost</th>
                    <th className="text-right py-2 px-3 border-b border-slate-700" style={{ width: '20%' }}>Client Price</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, itemIdx) => (
                    <tr 
                      key={itemIdx} 
                      className="border-b"
                      style={{ borderColor: '#2f2f2f' }}
                    >
                      {/* Item Name with TEXT WRAPPING ENABLED */}
                      <td 
                        className="py-3 px-3 text-sm text-slate-300"
                        style={{ 
                          whiteSpace: 'normal', 
                          wordWrap: 'break-word',
                          lineHeight: '1.4',
                        }}
                      >
                        {item.itemName}
                      </td>
                      {/* Quantity - VERTICALLY CENTERED */}
                      <td 
                        className="py-3 px-2 text-sm text-center font-bold text-white"
                        style={{ verticalAlign: 'middle' }}
                      >
                        {item.quantity}
                      </td>
                      {/* Unit - VERTICALLY CENTERED */}
                      <td 
                        className="py-3 px-2 text-sm text-center text-slate-500"
                        style={{ verticalAlign: 'middle' }}
                      >
                        {item.unit || 'EA'}
                      </td>
                      {/* Builder Cost */}
                      <td 
                        className="py-3 px-3 text-sm text-right text-slate-400"
                        style={{ verticalAlign: 'middle' }}
                      >
                        {formatCurrency(item.builderCost)}
                      </td>
                      {/* Client Price */}
                      <td 
                        className="py-3 px-3 text-sm text-right font-medium text-emerald-400"
                        style={{ verticalAlign: 'middle' }}
                      >
                        {formatCurrency(item.clientPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* CATEGORY SUBTOTAL ROW */}
                <tfoot>
                  <tr style={{ backgroundColor: '#252525' }}>
                    <td 
                      colSpan={3} 
                      className="py-2 px-3 text-xs font-bold text-slate-400 uppercase tracking-wider text-right"
                    >
                      {category} Subtotal:
                    </td>
                    <td className="py-2 px-3 text-sm text-right font-semibold text-slate-300">
                      {formatCurrency(categoryBuilderTotal)}
                    </td>
                    <td className="py-2 px-3 text-sm text-right font-bold text-emerald-400">
                      {formatCurrency(categoryClientTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })}

        {/* Grand Total Footer */}
        <div 
          className="mt-6 pt-4 border-t-2"
          style={{ borderColor: '#10b981' }}
        >
          <div className="flex justify-between items-center mb-2">
            <div>
              <span className="text-sm font-bold text-slate-200 uppercase tracking-wider">
                Total Materials
              </span>
              <span className="ml-2 text-xs text-slate-500">
                ({materialItems.length} items across {sortedTradeCategories.length} trades)
              </span>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-400">
              <span className="uppercase tracking-wider">Builder Cost:</span>
              <span className="ml-2 font-semibold text-slate-200">{formatCurrency(grandTotalBuilder)}</span>
            </div>
            <div className="text-right">
              <span className="text-xs text-slate-500 uppercase tracking-wider mr-2">Client Price:</span>
              <span 
                className="text-2xl font-black"
                style={{ color: '#10b981' }}
              >
                {formatCurrency(grandTotalClient)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Footer Note */}
        <div className="mt-4 text-xs text-slate-600 text-center">
          Generated by Contractor Overwatch • Prices subject to supplier confirmation • Tier 1 Estimating Standards
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SCOPE OF WORK (The Shield) - with Exclusions & Terms
  // ═══════════════════════════════════════════════════════════════════════════
  const renderScopeOfWork = () => {
    const groups = estimateData?.groups || [];
    const totalCost = estimateData?.totalCost || 0;
    
    return (
      <div className="bg-white text-slate-900 p-8 min-h-[800px]">
        <div className="bg-purple-600 text-white px-6 py-4 rounded-t-lg -mx-8 -mt-8 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold">SCOPE OF WORK</h1>
              <p className="text-purple-200 text-sm">Binding Contract Document</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-purple-200">Contract #</p>
              <p className="font-semibold">SOW-{Date.now().toString().slice(-6)}</p>
            </div>
          </div>
        </div>

        {/* Included Work */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            Scope of Work - Included Items
          </h2>
          {groups.map((group, idx) => (
            <div key={idx} className="mb-4">
              <h3 className="font-bold text-slate-800 bg-slate-100 px-3 py-2 rounded">{group.name}</h3>
              <ul className="mt-2 pl-4 space-y-1">
                {(group.items || []).map((item, itemIdx) => (
                  <li key={itemIdx} className="text-sm text-slate-700 flex justify-between">
                    <span className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />
                      {item.itemName} {item.quantity > 1 && `(Qty: ${item.quantity})`}
                    </span>
                    <span className="text-slate-500">{formatCurrency(item.unitCost * item.quantity)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* EXCLUSIONS & ASSUMPTIONS - Critical Section */}
        <div className="mb-8 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
          <h2 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Exclusions & Assumptions (NOT Included)
          </h2>
          <ul className="space-y-2 text-sm text-red-900">
            <li className="flex items-start gap-2">
              <X className="h-4 w-4 text-red-500 mt-0.5" />
              <span><strong>Structural modifications</strong> - Any load-bearing wall changes require separate engineering review</span>
            </li>
            <li className="flex items-start gap-2">
              <X className="h-4 w-4 text-red-500 mt-0.5" />
              <span><strong>Hidden damage</strong> - Water damage, mold, or rot discovered during work will require Change Order</span>
            </li>
            <li className="flex items-start gap-2">
              <X className="h-4 w-4 text-red-500 mt-0.5" />
              <span><strong>Permits & inspections</strong> - Unless explicitly listed, permit fees are client responsibility</span>
            </li>
            <li className="flex items-start gap-2">
              <X className="h-4 w-4 text-red-500 mt-0.5" />
              <span><strong>Furniture moving</strong> - Client responsible for clearing work areas before start date</span>
            </li>
            <li className="flex items-start gap-2">
              <X className="h-4 w-4 text-red-500 mt-0.5" />
              <span><strong>Landscaping repair</strong> - Exterior work may require separate landscaping restoration</span>
            </li>
          </ul>
        </div>

        {/* CHANGE ORDER PROCEDURES */}
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h2 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            Change Order Procedures
          </h2>
          <ol className="space-y-2 text-sm text-amber-900 list-decimal list-inside">
            <li>All changes to scope must be submitted in writing</li>
            <li>Contractor will provide written Change Order with updated pricing within 48 hours</li>
            <li>Work on changes will not begin until Change Order is signed by both parties</li>
            <li>Changes may affect project timeline - updated schedule will be provided with Change Order</li>
            <li>Emergency changes requiring immediate action may be verbally approved but must be documented within 24 hours</li>
          </ol>
        </div>

        {/* PAYMENT SCHEDULE */}
        <div className="mb-8 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <h2 className="text-lg font-bold text-emerald-800 mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Payment Schedule
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-emerald-700 border-b border-emerald-200">
                <th className="text-left py-2">Milestone</th>
                <th className="text-center py-2">Percentage</th>
                <th className="text-right py-2">Amount</th>
              </tr>
            </thead>
            <tbody className="text-emerald-900">
              <tr className="border-b border-emerald-100">
                <td className="py-2">Deposit (Upon Contract Signing)</td>
                <td className="py-2 text-center">50%</td>
                <td className="py-2 text-right font-semibold">{formatCurrency(totalCost * 0.5)}</td>
              </tr>
              <tr className="border-b border-emerald-100">
                <td className="py-2">Progress Payment (Rough-In Complete)</td>
                <td className="py-2 text-center">25%</td>
                <td className="py-2 text-right font-semibold">{formatCurrency(totalCost * 0.25)}</td>
              </tr>
              <tr>
                <td className="py-2">Final Payment (Upon Completion)</td>
                <td className="py-2 text-center">25%</td>
                <td className="py-2 text-right font-semibold">{formatCurrency(totalCost * 0.25)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-emerald-300">
                <td className="py-2 font-bold">Total Contract Value</td>
                <td className="py-2 text-center font-bold">100%</td>
                <td className="py-2 text-right font-bold text-lg">{formatCurrency(totalCost)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Signatures */}
        <div className="mt-8 pt-8 border-t-2 border-slate-200">
          <p className="text-sm text-slate-600 mb-6">
            By signing below, both parties agree to the scope, exclusions, change order procedures, and payment terms outlined in this document.
          </p>
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-xs text-slate-500 mb-8">Client Signature</p>
              <div className="border-b border-slate-400 mb-2" />
              <p className="text-sm">{clientInfo?.name || "Client"}</p>
              <p className="text-xs text-slate-500 mt-2">Date: _____________</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-8">Contractor Signature</p>
              <div className="border-b border-slate-400 mb-2" />
              <p className="text-sm">Contractor Name</p>
              <p className="text-xs text-slate-500 mt-2">Date: _____________</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // BUILD OPS ORDER - Complete Project Execution Plan
  // Combines Main Estimate + All Change Orders with Time Estimates
  // ═══════════════════════════════════════════════════════════════════════════
  const renderBuildOpsOrder = () => {
    // Get all scopes from estimateData
    const baseGroups = estimateData?.groups || [];
    const changeOrders = estimateData?.changeOrders || [];
    const allScopes = estimateData?.allScopes || { base: { groups: baseGroups }, changeOrders: changeOrders };
    
    // Calculate totals for base estimate
    const calculateScopeTotals = (groups) => {
      let builderTotal = 0;
      let clientTotal = 0;
      let itemCount = 0;
      
      (groups || []).forEach(group => {
        (group.items || []).forEach(item => {
          const qty = parseFloat(item.quantity) || 0;
          const unitCost = parseFloat(item.unitCost) || 0;
          const markup = parseFloat(item.markupPercent) || 20;
          const base = qty * unitCost;
          builderTotal += base;
          clientTotal += base * (1 + markup / 100);
          itemCount++;
        });
      });
      
      return { builderTotal, clientTotal, itemCount };
    };
    
    // Estimate time for each trade category (hours)
    const estimateTradeTime = (groupName, itemCount) => {
      const name = (groupName || '').toUpperCase();
      const baseHours = Math.max(4, itemCount * 0.5); // Minimum 4 hours per section
      
      if (name.includes('DEMO')) return { hours: baseHours * 0.8, phase: 'Phase 1 - Demo' };
      if (name.includes('FRAMING') || name.includes('STRUCTURE')) return { hours: baseHours * 1.5, phase: 'Phase 2 - Structure' };
      if (name.includes('PLUMB')) return { hours: baseHours * 1.2, phase: 'Phase 3 - Rough-In' };
      if (name.includes('ELEC')) return { hours: baseHours * 1.2, phase: 'Phase 3 - Rough-In' };
      if (name.includes('HVAC')) return { hours: baseHours * 1.0, phase: 'Phase 3 - Rough-In' };
      if (name.includes('INSUL')) return { hours: baseHours * 0.6, phase: 'Phase 4 - Insulation' };
      if (name.includes('DRYWALL')) return { hours: baseHours * 1.3, phase: 'Phase 5 - Drywall' };
      if (name.includes('PAINT')) return { hours: baseHours * 1.0, phase: 'Phase 6 - Finishes' };
      if (name.includes('FLOOR') || name.includes('TILE')) return { hours: baseHours * 1.4, phase: 'Phase 6 - Finishes' };
      if (name.includes('TRIM') || name.includes('CABINET')) return { hours: baseHours * 1.2, phase: 'Phase 7 - Trim' };
      if (name.includes('FIXTURE')) return { hours: baseHours * 0.8, phase: 'Phase 8 - Final' };
      return { hours: baseHours, phase: 'General Work' };
    };
    
    const baseTotals = calculateScopeTotals(allScopes.base?.groups || baseGroups);
    
    // Calculate change order totals
    const coTotals = (allScopes.changeOrders || changeOrders).map((co, idx) => ({
      number: co.number || idx + 1,
      name: co.name || `Change Order #${idx + 1}`,
      ...calculateScopeTotals(co.groups),
      groups: co.groups || [],
    }));
    
    const grandTotal = {
      builderTotal: baseTotals.builderTotal + coTotals.reduce((sum, co) => sum + co.builderTotal, 0),
      clientTotal: baseTotals.clientTotal + coTotals.reduce((sum, co) => sum + co.clientTotal, 0),
      itemCount: baseTotals.itemCount + coTotals.reduce((sum, co) => sum + co.itemCount, 0),
    };
    
    // Calculate total project hours
    let totalProjectHours = 0;
    const phaseHours = {};
    
    (allScopes.base?.groups || baseGroups).forEach(group => {
      const timeEst = estimateTradeTime(group.name, (group.items || []).length);
      totalProjectHours += timeEst.hours;
      phaseHours[timeEst.phase] = (phaseHours[timeEst.phase] || 0) + timeEst.hours;
    });
    
    coTotals.forEach(co => {
      (co.groups || []).forEach(group => {
        const timeEst = estimateTradeTime(group.name, (group.items || []).length);
        totalProjectHours += timeEst.hours;
        phaseHours[timeEst.phase] = (phaseHours[timeEst.phase] || 0) + timeEst.hours;
      });
    });
    
    const totalWeeks = Math.ceil(totalProjectHours / 40); // 40-hour work weeks
    
    return (
      <div 
        className="min-h-[800px] p-6"
        style={{ 
          backgroundColor: '#0f172a',
          fontFamily: "'Rajdhani', sans-serif",
        }}
      >
        {/* Header - Military Style */}
        <div 
          className="-mx-6 -mt-6 mb-6 px-6 py-5"
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderBottom: '4px solid #047857',
          }}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div 
                className="w-14 h-14 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                <HardHat className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white tracking-wider uppercase">
                  BUILD OPS ORDER
                </h1>
                <p className="text-emerald-100 text-sm tracking-widest uppercase">
                  Complete Project Execution Plan
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-emerald-200 uppercase tracking-wider">Order Date</p>
              <p className="text-lg font-bold text-white">{formatDate()}</p>
              <p className="text-xs text-emerald-200 mt-1">Est. Duration: {totalWeeks} Weeks</p>
            </div>
          </div>
        </div>

        {/* Client & Project Info */}
        <div 
          className="mb-6 p-4 rounded-lg"
          style={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
          }}
        >
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Client</p>
              <p className="text-lg font-bold text-white">{clientInfo?.name || "Client Name"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Project Site</p>
              <p className="text-sm text-slate-300">{clientInfo?.address || "TBD"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Contact</p>
              <p className="text-sm text-slate-300">{clientInfo?.phone || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div 
          className="mb-6 p-4 rounded-lg"
          style={{
            backgroundColor: 'rgba(30, 41, 59, 0.8)',
            border: '1px solid #334155',
          }}
        >
          <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Financial Summary
          </h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center p-3 rounded bg-slate-900/50">
              <p className="text-xs text-slate-500 uppercase">Main Estimate</p>
              <p className="text-xl font-bold text-emerald-400">{formatCurrency(baseTotals.clientTotal)}</p>
              <p className="text-xs text-slate-500">{baseTotals.itemCount} items</p>
            </div>
            {coTotals.length > 0 && (
              <div className="text-center p-3 rounded bg-slate-900/50">
                <p className="text-xs text-slate-500 uppercase">Change Orders ({coTotals.length})</p>
                <p className="text-xl font-bold text-amber-400">
                  {formatCurrency(coTotals.reduce((sum, co) => sum + co.clientTotal, 0))}
                </p>
                <p className="text-xs text-slate-500">
                  {coTotals.reduce((sum, co) => sum + co.itemCount, 0)} items
                </p>
              </div>
            )}
            <div className="text-center p-3 rounded bg-emerald-500/20 border border-emerald-500/30">
              <p className="text-xs text-emerald-400 uppercase">Grand Total</p>
              <p className="text-2xl font-black text-emerald-400">{formatCurrency(grandTotal.clientTotal)}</p>
              <p className="text-xs text-emerald-300">{grandTotal.itemCount} total items</p>
            </div>
            <div className="text-center p-3 rounded bg-blue-500/20 border border-blue-500/30">
              <p className="text-xs text-blue-400 uppercase">Est. Duration</p>
              <p className="text-2xl font-black text-blue-400">{totalWeeks}</p>
              <p className="text-xs text-blue-300">weeks (~{Math.round(totalProjectHours)} hrs)</p>
            </div>
          </div>
        </div>

        {/* Project Timeline by Phase */}
        <div 
          className="mb-6 p-4 rounded-lg"
          style={{
            backgroundColor: 'rgba(30, 41, 59, 0.8)',
            border: '1px solid #334155',
          }}
        >
          <h3 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Project Timeline by Phase
          </h3>
          <div className="space-y-2">
            {Object.entries(phaseHours).sort((a, b) => {
              const order = ['Phase 1', 'Phase 2', 'Phase 3', 'Phase 4', 'Phase 5', 'Phase 6', 'Phase 7', 'Phase 8'];
              return order.findIndex(p => a[0].includes(p)) - order.findIndex(p => b[0].includes(p));
            }).map(([phase, hours], idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-32 text-xs text-slate-400">{phase}</div>
                <div className="flex-1 h-4 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, (hours / totalProjectHours) * 100)}%`,
                      background: idx < 2 ? 'linear-gradient(90deg, #ef4444, #f97316)' :
                                  idx < 4 ? 'linear-gradient(90deg, #f59e0b, #eab308)' :
                                  'linear-gradient(90deg, #10b981, #14b8a6)',
                    }}
                  />
                </div>
                <div className="w-16 text-xs text-right text-slate-400">{Math.round(hours)} hrs</div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Estimate Scope */}
        <div className="mb-6">
          <div 
            className="px-4 py-3 rounded-t-lg flex items-center justify-between"
            style={{ 
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(16, 185, 129, 0.1))',
              borderBottom: '2px solid #10b981',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-emerald-500/30 flex items-center justify-center">
                <FileText className="h-4 w-4 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-emerald-400 uppercase tracking-wider">Main Estimate</h3>
                <p className="text-xs text-slate-400">{baseTotals.itemCount} items • {formatCurrency(baseTotals.clientTotal)}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-b-lg border border-slate-700 border-t-0">
            {(allScopes.base?.groups || baseGroups).map((group, idx) => {
              const timeEst = estimateTradeTime(group.name, (group.items || []).length);
              return (
                <div key={idx} className="mb-4 last:mb-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-white">{group.name}</h4>
                    <span className="text-xs text-slate-500">~{Math.round(timeEst.hours)} hrs</span>
                  </div>
                  <div className="pl-4 space-y-1">
                    {(group.items || []).slice(0, 5).map((item, itemIdx) => (
                      <div key={itemIdx} className="flex items-center justify-between text-xs">
                        <span className="text-slate-400 flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                          {item.itemName}
                        </span>
                        <span className="text-slate-500">{item.quantity} {item.unit}</span>
                      </div>
                    ))}
                    {(group.items || []).length > 5 && (
                      <p className="text-xs text-slate-600 italic">
                        +{(group.items || []).length - 5} more items...
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Change Orders */}
        {coTotals.length > 0 && coTotals.map((co, coIdx) => (
          <div key={coIdx} className="mb-6">
            <div 
              className="px-4 py-3 rounded-t-lg flex items-center justify-between"
              style={{ 
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(245, 158, 11, 0.1))',
                borderBottom: '2px solid #f59e0b',
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-amber-500/30 flex items-center justify-center">
                  <GitCompare className="h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-amber-400 uppercase tracking-wider">
                    Change Order #{co.number}
                  </h3>
                  <p className="text-xs text-slate-400">{co.itemCount} items • {formatCurrency(co.clientTotal)}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-900/50 p-4 rounded-b-lg border border-slate-700 border-t-0">
              {(co.groups || []).map((group, idx) => {
                const timeEst = estimateTradeTime(group.name, (group.items || []).length);
                return (
                  <div key={idx} className="mb-4 last:mb-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-bold text-white">{group.name}</h4>
                      <span className="text-xs text-slate-500">~{Math.round(timeEst.hours)} hrs</span>
                    </div>
                    <div className="pl-4 space-y-1">
                      {(group.items || []).slice(0, 5).map((item, itemIdx) => (
                        <div key={itemIdx} className="flex items-center justify-between text-xs">
                          <span className="text-slate-400 flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-amber-500" />
                            {item.itemName}
                          </span>
                          <span className="text-slate-500">{item.quantity} {item.unit}</span>
                        </div>
                      ))}
                      {(group.items || []).length > 5 && (
                        <p className="text-xs text-slate-600 italic">
                          +{(group.items || []).length - 5} more items...
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Footer - Signatures */}
        <div 
          className="mt-8 pt-6 border-t-2"
          style={{ borderColor: '#10b981' }}
        >
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-6">Authorized By</p>
              <div className="border-b border-slate-600 mb-2 h-8" />
              <p className="text-sm text-slate-300">Project Manager</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-6">Date Issued</p>
              <div className="border-b border-slate-600 mb-2 h-8" />
              <p className="text-sm text-slate-300">{formatDate()}</p>
            </div>
          </div>
          <p className="text-xs text-slate-600 text-center mt-6">
            Generated by Contractor Overwatch • Build Ops Order • {formatDate()}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 2147483646 }} // Just below Site Superintendent, but above EVERYTHING else
    >
      {/* Backdrop - Solid & Blocking - covers entire screen including sidebar */}
      <div 
        className="absolute inset-0 bg-black backdrop-blur-sm"
        style={{ opacity: 0.85, pointerEvents: 'all' }}
        onClick={onClose}
      />
      {/* Modal Content */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-${config.color}-500/20 border border-${config.color}-500/40 flex items-center justify-center`}>
              <IconComponent className={`h-5 w-5 text-${config.color}-400`} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{config.title}</h2>
              <p className="text-xs text-slate-400">{config.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 py-2 border-b border-slate-800 flex gap-4">
          <button
            onClick={() => setActiveTab("preview")}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === "preview" ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            Preview
          </button>
          <button
            onClick={() => setActiveTab("send")}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === "send" ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            Send / Share
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {activeTab === "preview" ? (
            <div className="p-6">
              <div ref={contentRef} className="bg-white rounded-lg shadow-lg overflow-hidden">
                {renderDocumentContent()}
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={() => handleSend("email")}
                  disabled={isSending}
                  className="flex flex-col items-center gap-3 p-6 bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-700 transition-colors"
                >
                  {isSending && sendMethod === "email" ? (
                    <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
                  ) : (
                    <Mail className="h-8 w-8 text-blue-400" />
                  )}
                  <span className="font-semibold text-white">Send via Email</span>
                  <span className="text-xs text-slate-400">Direct to client inbox</span>
                </button>

                <button
                  onClick={() => handleSend("sms")}
                  disabled={isSending}
                  className="flex flex-col items-center gap-3 p-6 bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-700 transition-colors"
                >
                  {isSending && sendMethod === "sms" ? (
                    <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
                  ) : (
                    <MessageSquare className="h-8 w-8 text-emerald-400" />
                  )}
                  <span className="font-semibold text-white">Send via SMS</span>
                  <span className="text-xs text-slate-400">Text message with link</span>
                </button>

                <button
                  onClick={handleCopyLink}
                  className="flex flex-col items-center gap-3 p-6 bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-700 transition-colors"
                >
                  {isCopied ? (
                    <Check className="h-8 w-8 text-emerald-400" />
                  ) : (
                    <Link2 className="h-8 w-8 text-purple-400" />
                  )}
                  <span className="font-semibold text-white">{isCopied ? "Link Copied!" : "Copy Link"}</span>
                  <span className="text-xs text-slate-400">Share anywhere</span>
                </button>
              </div>

              <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <h4 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Smart Tracking Enabled
                </h4>
                <p className="text-xs text-slate-400 mt-1">
                  You&apos;ll be notified when the client opens this document. Status will change from &quot;Sent&quot; to &quot;Viewed&quot; (Hot Lead).
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700 flex justify-between">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
            Cancel
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
            <button
              onClick={onExport}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors font-semibold"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
